import type { ApiClient } from './ApiClient';

// Formes alignées sur POST /auth/register et POST /auth/login (AUT-01, apps/api/src/
// routes/auth.ts) — seule source de vérité pour ces routes. `passwordHash` n'existe
// jamais côté client, comme pour `packages/types` (voir son commentaire sur `User`).
export interface AuthUser {
  id: string;
  email: string;
  // AUT-17 : toujours renseigné (dérivé de firstName/lastName quand les deux
  // sont connus) — voir apps/api/src/lib/userName.ts.
  fullName: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
  emailVerified: boolean;
  // AUT-05 — voir apps/api/src/lib/user.ts#selectSafeUser.
  mfaEnabled: boolean;
}

export interface RegisterPayload {
  email: string;
  password: string;
  // ⚠️ AUT-17 : `firstName` + `lastName` est la forme À UTILISER pour toute
  // NOUVELLE inscription — c'est ce qu'envoient les deux `Register.tsx`. Le
  // serveur en DÉRIVE `fullName`, qu'il ne faut alors PAS transmettre.
  firstName?: string;
  lastName?: string;
  // Forme HISTORIQUE, conservée pour rétrocompatibilité (appelants qui ne
  // connaissent pas encore le couple prénom/nom) : `firstName`/`lastName`
  // restent alors `null` en base, jamais devinés depuis cette chaîne.
  fullName?: string;
  // PAR-03 : optionnel — présent UNIQUEMENT pour l'inscription parent (web-parent),
  // crée le profil `Parent` dans la MÊME transaction que le `User` côté serveur
  // (POST /auth/register, apps/api/src/routes/auth.ts) — voir docs/architecture.md,
  // section "PAR-03", pour le bug réel (User orphelin) que ce champ corrige.
  phone?: string;
  // CMP-02 : REQUIS (pas optionnel) — le backend refuse (400 consent_required)
  // toute valeur différente de `true` au sens strict. Reflète l'état d'une case à
  // cocher JAMAIS pré-cochée côté formulaire (Register.tsx, web-admin ET
  // web-parent) — voir docs/architecture.md, section "CMP-02".
  consentAccepted: boolean;
}

export interface RegisterResponse {
  user: AuthUser;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  user: AuthUser;
}

// AUT-05 — renvoyée par POST /auth/login À LA PLACE de LoginResponse quand le mot de
// passe est correct MAIS le compte a le MFA activé : aucun token émis à ce stade
// (voir apps/api/src/routes/auth.ts), `mfaToken` doit être transmis à
// `verifyMfaLogin()` ci-dessous avec un code TOTP/de secours pour compléter la
// connexion.
export interface MfaChallengeResponse {
  mfaRequired: true;
  mfaToken: string;
}

// CMP-02 — renvoyée par POST /auth/login OU POST /auth/mfa/verify À LA PLACE de
// LoginResponse quand la bascule `CONSENT_ENFORCEMENT_ENABLED` (défaut : désactivée,
// voir apps/api/src/lib/consentEnforcement.ts) est active ET que le compte n'a pas
// encore accepté la version ACTUELLE de la politique de confidentialité : aucun
// token émis à ce stade, `consentToken` doit être transmis à `acceptConsent()`
// ci-dessous pour compléter la connexion. ⚠️ Émise SEULEMENT après un éventuel MFA
// déjà résolu (voir le commentaire de `resolveSessionOrConsentChallenge()` côté
// backend, apps/api/src/routes/auth.ts) — jamais avant.
export interface ConsentChallengeResponse {
  consentRequired: true;
  consentToken: string;
  policyVersion: string;
}

// `login()` peut renvoyer L'UNE OU L'AUTRE forme — le composant appelant (voir
// apps/web-admin/src/pages/Login.tsx) doit tester `'mfaRequired' in result` PUIS
// `'consentRequired' in result` avant de lire `accessToken`. web-parent n'a
// structurellement JAMAIS de compte MFA (seuls les SCHOOL_ADMIN peuvent l'activer)
// mais PEUT recevoir un défi de consentement comme n'importe quel autre compte.
export type LoginResult = LoginResponse | MfaChallengeResponse | ConsentChallengeResponse;

// Copie manuelle de `MembershipRole` (apps/api/prisma/schema.prisma) — les enums
// générés par Prisma restent internes à apps/api, jamais réexportés tels quels vers
// les packages front-end (voir CLAUDE.md, "apps/api" ; même pattern déjà établi pour
// les autres enums synchronisés à la main entre Prisma et packages/types). À tenir à
// jour manuellement si `MembershipRole` change côté schéma. AUT-12 : `ADMIN_FINANCIER`
// ajouté (rôle dédié à la gestion financière). AUT-27 : `GESTION_ELEVES` ajouté
// (rôle dédié à Élèves & Inscriptions).
// SCH-53 : `TEACHER` ajouté (infrastructure de rôle cumulable uniquement,
// aucun accès financier — voir apps/api/src/lib/permissions.ts#TEACHER_ROLES).
export type MembershipRole = 'SCHOOL_ADMIN' | 'STAFF' | 'CASHIER' | 'ADMIN_FINANCIER' | 'GESTION_ELEVES' | 'TEACHER';

// Forme alignée sur GET /me (AUT-03, apps/api/src/routes/me.ts). `role`/`tenantId`
// sont `null` pour un utilisateur sans Membership (0 Membership — voir
// docs/architecture.md, section "AUT-03") : ce n'est PAS une erreur, juste l'état
// "pas encore de rattachement à une école". `hasParentProfile` est une simple
// information (existence d'un profil Parent lié), sans logique de blocage associée
// ici — posée pour PAR-01.
export interface MeResponse {
  user: AuthUser;
  // AUT-26 : `role` reste le DÉRIVÉ (roles[0]) pour compatibilité — la plupart des
  // consommateurs ne font qu'un contrôle `=== null`. `roles` (union d'accès) est
  // la source de vérité pour toute décision de filtrage réel (Sidebar, etc.).
  role: MembershipRole | null;
  roles: MembershipRole[];
  tenantId: string | null;
  hasParentProfile: boolean;
  // AUT-25 : `true` UNIQUEMENT si `role === null` ET ce compte a déjà eu un
  // Membership révoqué — distingue "jamais eu d'accès" (AccessDenied.tsx,
  // Variante 1) de "accès révoqué" (Variante 2).
  revoked: boolean;
}

// Fonctions autonomes plutôt que des méthodes sur `ApiClient` : `ApiClient` reste un
// client HTTP générique (get/post/put/patch/delete), sans connaissance du domaine
// auth — même séparation que le reste de ce package. Le refresh token (cookie
// httpOnly émis par le backend) n'apparaît jamais ici : rien à lire ni à transmettre
// explicitement côté client, le navigateur s'en charge (voir docs/architecture.md,
// section "AUT-01").
export function register(client: ApiClient, payload: RegisterPayload): Promise<RegisterResponse> {
  return client.post<RegisterResponse>('/auth/register', payload);
}

export function login(client: ApiClient, payload: LoginPayload): Promise<LoginResult> {
  return client.post<LoginResult>('/auth/login', payload);
}

// AUT-05 — body attendu par POST /auth/mfa/verify (apps/api/src/routes/auth.ts) :
// `code` accepté sous DEUX formes (code TOTP à 6 chiffres OU code de secours
// `XXXX-XXXX`), distinguées par leur FORMAT côté serveur — rien à préciser ici.
export interface VerifyMfaLoginPayload {
  mfaToken: string;
  code: string;
}

// Second temps de la connexion quand `login()` a renvoyé `MfaChallengeResponse` —
// CMP-02 : peut désormais renvoyer un `ConsentChallengeResponse` À LA PLACE d'une
// session complète (le MFA vient d'être résolu, mais le compte doit encore
// (ré-)accepter la politique de confidentialité) — l'appelant doit tester
// `'consentRequired' in result` avant de lire `accessToken`, même discipline que
// `login()` pour `mfaRequired` ci-dessus.
export function verifyMfaLogin(
  client: ApiClient,
  payload: VerifyMfaLoginPayload,
): Promise<LoginResponse | ConsentChallengeResponse> {
  return client.post<LoginResponse | ConsentChallengeResponse>('/auth/mfa/verify', payload);
}

// CMP-02 — second temps de la connexion quand `login()`/`verifyMfaLogin()` ont
// renvoyé `ConsentChallengeResponse` : `consentToken` transmis TEL QUEL, aucun
// booléen de confirmation supplémentaire à envoyer (l'ACTE d'appeler cette fonction,
// depuis un écran dont le bouton reste désactivé tant que la case n'est PAS cochée,
// EST le consentement explicite — voir apps/api/src/routes/auth.ts, commentaire de
// POST /auth/consent/accept). Renvoie TOUJOURS `LoginResponse` (jamais un second
// défi) : un succès ici complète définitivement la connexion.
export interface AcceptConsentPayload {
  consentToken: string;
}

export function acceptConsent(client: ApiClient, payload: AcceptConsentPayload): Promise<LoginResponse> {
  return client.post<LoginResponse>('/auth/consent/accept', payload);
}

// CMP-02 — GET /privacy-policy (apps/api/src/routes/privacyPolicy.ts) : PUBLIC,
// aucun `accessToken` — consommée par le formulaire d'inscription (visiteur pas
// encore authentifié) ET l'écran de re-sollicitation (l'utilisateur n'a, à ce
// stade, qu'un `consentToken` de courte durée, pas encore de session).
export interface PrivacyPolicyResponse {
  version: string;
  text: string;
}

export function getPrivacyPolicy(client: ApiClient): Promise<PrivacyPolicyResponse> {
  return client.get<PrivacyPolicyResponse>('/privacy-policy');
}

// AUT-05 — formes alignées sur POST /mfa/totp/setup et /confirm
// (apps/api/src/routes/mfa.ts). `accessToken` explicite (comme `getMe()` ci-dessus) :
// ces deux appels ont lieu juste après une connexion/pendant l'activation MFA, avant
// que l'état React ait forcément propagé le token le plus récent.
export interface TotpSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

export function setupTotp(client: ApiClient, accessToken: string): Promise<TotpSetupResponse> {
  return client.post<TotpSetupResponse>('/mfa/totp/setup', undefined, { accessToken });
}

export interface ConfirmTotpPayload {
  code: string;
}

export interface ConfirmTotpResponse {
  backupCodes: string[];
}

export function confirmTotp(
  client: ApiClient,
  accessToken: string,
  payload: ConfirmTotpPayload,
): Promise<ConfirmTotpResponse> {
  return client.post<ConfirmTotpResponse>('/mfa/totp/confirm', payload, { accessToken });
}

// `accessToken` explicite plutôt que de dépendre de `getAuthToken` (config
// ApiClient) : appelée juste après un `login()` réussi (AUT-03, voir
// packages/ui/src/auth/AuthContext.tsx), au moment où le token fraîchement émis
// n'est pas forcément déjà reflété par un `getAuthToken` branché sur un état React
// (mise à jour asynchrone) — voir `RequestOptions.accessToken` dans ApiClient.ts.
export function getMe(client: ApiClient, accessToken: string): Promise<MeResponse> {
  return client.get<MeResponse>('/me', { accessToken });
}

// AUT-06 : forme IDENTIQUE à LoginResponse (POST /auth/refresh renvoie exactement ce
// que POST /auth/login renvoie, un nouvel accessToken + le user courant) — pas de
// type dédié, réutilise LoginResponse directement pour éviter deux types qui
// devraient rester synchronisés artificiellement.
//
// Aucun paramètre autre que `client` : le refresh token vit dans le cookie httpOnly
// posé par POST /auth/login, envoyé automatiquement par le navigateur
// (`credentials: 'include'`, voir ApiClient.request()) — rien à lire ni à transmettre
// explicitement ici, même principe que documenté sur `register()`/`login()`
// ci-dessus. Appelée PROACTIVEMENT au montage de `<AuthProvider>`
// (packages/ui/src/auth/AuthContext.tsx) pour restaurer la session après un
// rechargement de page — c'est l'appel dont l'ABSENCE causait le bug AUT-06 ("un F5
// force une reconnexion complète").
export function refresh(client: ApiClient): Promise<LoginResponse> {
  return client.post<LoginResponse>('/auth/refresh');
}

export interface LogoutResponse {
  message: string;
}

// AUT-06 : contrairement à un simple effacement de la mémoire JS locale (state React
// dans AuthContext.tsx), cet appel est ce qui invalide RÉELLEMENT le cookie httpOnly
// côté serveur — un script frontend ne peut structurellement PAS lire ni effacer un
// cookie httpOnly lui-même (c'est tout le principe de cet attribut, voir AUT-01) :
// seule une réponse `Set-Cookie` du serveur peut le faire. Sans cet appel, un
// rechargement de page juste après un "logout" côté client aurait silencieusement
// restauré la session (le cookie httpOnly, lui, resterait valide jusqu'à ses 7 jours).
export function logout(client: ApiClient): Promise<LogoutResponse> {
  return client.post<LogoutResponse>('/auth/logout');
}

// Formes alignées sur POST /auth/forgot-password et POST /auth/reset-password
// (AUT-02, apps/api/src/routes/auth.ts) — seule source de vérité pour ces routes.
export interface ForgotPasswordPayload {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
}

export interface ResetPasswordResponse {
  message: string;
}

// Réponse TOUJOURS 200 avec un message générique, que l'email existe ou non en base
// (anti-énumération, AUT-02) — rien à distinguer côté appelant, le composant qui
// consomme ceci affiche `message` tel quel, comme pour `login()`/`register()`.
export function forgotPassword(client: ApiClient, payload: ForgotPasswordPayload): Promise<ForgotPasswordResponse> {
  return client.post<ForgotPasswordResponse>('/auth/forgot-password', payload);
}

// N'authentifie PAS automatiquement (contrairement à `register()` + `login()`
// enchaînés côté web-admin/web-parent) — POST /auth/reset-password ne renvoie aucun
// token, par construction (AUT-02) : toutes les sessions actives de l'utilisateur
// sont révoquées par cet appel, se reconnecter explicitement ensuite est le
// comportement attendu, pas un oubli.
export function resetPassword(client: ApiClient, payload: ResetPasswordPayload): Promise<ResetPasswordResponse> {
  return client.post<ResetPasswordResponse>('/auth/reset-password', payload);
}

// AUT-16 — body attendu par POST /auth/change-password (apps/api/src/routes/
// auth.ts) : changer son mot de passe DEPUIS un compte DÉJÀ CONNECTÉ, DISTINCT
// de `resetPassword()` ci-dessus (par lien email, compte déconnecté).
export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

// Renvoie une session FRAÎCHE (même forme que `LoginResponse`/`refresh()`) —
// contrairement à `resetPassword()`, cette route RÉ-AUTHENTIFIE explicitement
// la session courante (nouveau `tv`, voir le doc-comment backend) : l'appelant
// (packages/ui/src/auth/AuthContext.tsx#changePassword) doit mettre à jour
// `accessToken`/`user` avec CE résultat, exactement comme `acceptConsent()`.
// `accessToken` explicite (comme `getMe()`/`setupTotp()` ci-dessus) : appelée
// depuis un écran déjà authentifié, pas de round-trip supplémentaire nécessaire
// pour connaître le token courant.
export function changePassword(
  client: ApiClient,
  payload: ChangePasswordPayload,
  accessToken: string,
): Promise<LoginResponse> {
  return client.post<LoginResponse>('/auth/change-password', payload, { accessToken });
}

// Forme du corps d'erreur renvoyé par TOUTES les routes /auth/* en cas d'échec (400,
// 401, 409, 429 — voir apps/api/src/routes/auth.ts) : `{ error, message }`. Exportée
// ici pour que web-admin/web-parent affichent `message` TEL QUEL (jamais reformulé —
// décision AUT-01, voir docs/architecture.md) sans dupliquer ce type de garde dans
// chaque app. `ApiError.body` est `unknown` par construction (ApiClient est générique,
// ignore la forme de n'importe quelle route) : ce garde restreint le type avant lecture.
export interface AuthErrorBody {
  error: string;
  message: string;
}

export function isAuthErrorBody(body: unknown): body is AuthErrorBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof (body as { message: unknown }).message === 'string'
  );
}
